import { useState, useEffect, useRef } from 'react'
import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'

const ROLES = [
  { value: 'decideur', label: 'Decideur' },
  { value: 'prescripteur', label: 'Prescripteur' },
  { value: 'administratif', label: 'Contact administratif' },
  { value: 'finance', label: 'Finance' },
  { value: 'autre', label: 'Autre' },
]

interface ContactResult {
  id: number
  name: string
  email: string
}

interface AddContactSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunityId: number
}

export function AddContactSheet({ open, onOpenChange, opportunityId }: AddContactSheetProps) {
  const [contactOpen, setContactOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ContactResult[]>([])
  const [selectedContact, setSelectedContact] = useState<ContactResult | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const form = useForm({
    opportunity_contact: {
      contact_id: '',
      role: '',
    },
  })

  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    const timer = setTimeout(() => {
      fetch(`/fundraising/opportunities/${opportunityId}/contacts/search?q=${encodeURIComponent(query)}`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      })
        .then((r) => r.json())
        .then((data) => setResults(data))
        .catch(() => {})
    }, 300)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query, opportunityId])

  function handleClose() {
    form.reset()
    setSelectedContact(null)
    setQuery('')
    setResults([])
    onOpenChange(false)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.post(`/fundraising/opportunities/${opportunityId}/opportunity_contacts`, {
      preserveScroll: true,
      onSuccess: handleClose,
    })
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-[420px] sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Ajouter un contact</SheetTitle>
          <SheetDescription>
            Associez un contact de l'organisation a cette opportunite.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {/* Contact picker */}
          <div className="space-y-2">
            <Label>Contact *</Label>
            <Popover open={contactOpen} onOpenChange={setContactOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal"
                >
                  {selectedContact ? selectedContact.name : 'Rechercher un contact...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[380px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Nom du contact..."
                    value={query}
                    onValueChange={setQuery}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {query.length < 2 ? 'Tapez au moins 2 caracteres' : 'Aucun contact trouve'}
                    </CommandEmpty>
                    <CommandGroup>
                      {results.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={String(c.id)}
                          onSelect={() => {
                            setSelectedContact(c)
                            form.setData('opportunity_contact', {
                              ...form.data.opportunity_contact,
                              contact_id: String(c.id),
                            })
                            setContactOpen(false)
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedContact?.id === c.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div>
                            <p className="text-sm font-medium">{c.name}</p>
                            {c.email && (
                              <p className="text-xs text-muted-foreground">{c.email}</p>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {form.errors['opportunity_contact.contact_id'] && (
              <p className="text-sm text-destructive">{form.errors['opportunity_contact.contact_id']}</p>
            )}
            {form.errors['opportunity_contact.contact'] && (
              <p className="text-sm text-destructive">{form.errors['opportunity_contact.contact']}</p>
            )}
          </div>

          {/* Role select */}
          <div className="space-y-2">
            <Label>Role *</Label>
            <Select
              value={form.data.opportunity_contact.role}
              onValueChange={(val) =>
                form.setData('opportunity_contact', {
                  ...form.data.opportunity_contact,
                  role: val,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisir un role..." />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.errors['opportunity_contact.role'] && (
              <p className="text-sm text-destructive">{form.errors['opportunity_contact.role']}</p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={form.processing || !selectedContact} className="flex-1">
              {form.processing ? 'Ajout...' : 'Ajouter'}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
